import { Inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";

import { Observable } from 'rxjs';

import { JSR310ConversionService } from '../../../shared/jsr310-localdates/services/jsr310-conversion.service';

import { Destinataire } from '../entities/destinataire.entity';
import { EnvelopeDocusignEntity } from "../entities/envelope-docusign.entity";
import { PlanPreventionEntity } from "../entities/plan-prevention.entity";
import { PlanPreventionTLR } from '../entities/plan-prevention-tlr.entity';
import { PdpExportCriteria } from "../entities/plan-prevention-export-criteria";
import { PdpSignatairesUpdate } from '../entities/pdp-signataires-update.entity';
import { SignatairePdpEntity } from '../entities/signataire-pdp.entity';

@Injectable({ providedIn: 'root' })
export class PlanPreventionService {
	private path: string;

	constructor( //
		@Inject(HttpClient) private httpClient: HttpClient, //
		@Inject(JSR310ConversionService) private jsr310Service: JSR310ConversionService, //
	) {
		this.path = (window as any).baseApiPath;
	}

	// Méthode permettant de créer un PDP
	public create(command: PlanPreventionEntity): Observable<PlanPreventionEntity> {
		const url = this.path + 'plans-preventions';
		const request = this.httpClient.post<PlanPreventionEntity>(url, command, {});
		return request;
	}

	// Méthode permettant de modifier un PDP
	public update(command: PlanPreventionEntity, step: string, incrementVersion: boolean): Observable<PlanPreventionEntity> {
		const url = this.path + 'plans-preventions/' + step + '/' + command.id + (incrementVersion ? '?incrementVersion=true' : '?incrementVersion=false');
		const request = this.httpClient.put<PlanPreventionEntity>(url, command, {});
		return request;
	}

	// Méthode permettant de mettre à jour le lien associé au PDP
	public updateLink(command: PlanPreventionEntity): Observable<PlanPreventionEntity> {
		const url = this.path + 'plans-preventions/url';
		const request = this.httpClient.post<PlanPreventionEntity>(url, command, {});
		return request;
	}
	
	// Méthode permettant de générer le PDF associé au PDP
	public generatePdf(formData: FormData): Observable<Blob> {
		let url = (window as any).uploadPath + '/pdp';
		return this.httpClient.post(url, formData, {responseType: 'blob'});
	}

	// Méthode permettant de recupérer le PDP mis en signature depuis docusign
	public loadDocusignDocument(envelopeId: string): Observable<Blob> {
		let url = this.path + 'plans-preventions/docusign-document/' + envelopeId;
		return this.httpClient.get(url, {responseType: 'blob'});
	}

	// Methode permettant le chargement des travaux pour un PDP
	public getTravaux(pdpId: number): Observable<PlanPreventionEntity> {
		const url = this.path + 'plans-preventions/travaux/' + pdpId;
		let result = this.httpClient.get<PlanPreventionEntity>(url);
		return result;
	}

	// Methode permettant le chargement des communication mutuelles pour un PDP
	public getCommunicationMutuelles(pdpId: number): Observable<PlanPreventionEntity> {
		const url = this.path + 'plans-preventions/communicationMutuelles/' + pdpId;
		let result = this.httpClient.get<PlanPreventionEntity>(url);
		return result;
	}

	// Methode permettant le chargement des signataires pour un PDP
	public getSignataires(pdpId: number): Observable<PlanPreventionEntity> {
		const url = this.path + 'plans-preventions/signataires/' + pdpId;
		let result = this.httpClient.get<PlanPreventionEntity>(url);
		return result;
	}

	// Methode permettant le chargement des valideurs pour un PDP
	public getValideurs(pdpId: number): Observable<PlanPreventionEntity> {
		const url = this.path + 'plans-preventions/valideurs/' + pdpId;
		let result = this.httpClient.get<PlanPreventionEntity>(url);
		return result;
	}
	
	// Methode permettant le chargement des valideurs signataires pour un PDP
	public getValideursEtRepresentants(pdpId: number): Observable<PlanPreventionEntity> {
		const url = this.path + 'plans-preventions/valideurs-et-representants/' + pdpId;
		let result = this.httpClient.get<PlanPreventionEntity>(url);
		return result;
	}

	// Methode permettant le chargement des signataires pour un PDP
	public getIntervenants(pdpId: number): Observable<PlanPreventionEntity> {
		const url = this.path + 'plans-preventions/intervenants/' + pdpId;
		let result = this.httpClient.get<PlanPreventionEntity>(url);
		return result;
	}

	// Methode permettant le chargement des nouveaux risques pour un PDP
	public getNouveauxRisques(pdpId: number): Observable<PlanPreventionEntity> {
		const url = this.path + 'plans-preventions/nouveaux-risques/' + pdpId;
		let result = this.httpClient.get<PlanPreventionEntity>(url);
		return result;
	}

	// Methode permettant le chargement des risques pour un PDP
	public getRisques(pdpId: number): Observable<PlanPreventionEntity> {
		const url = this.path + 'plans-preventions/risques/' + pdpId;
		let result = this.httpClient.get<PlanPreventionEntity>(url);
		return result;
	}

	// Methode permettant de recuperer la version courante du PDP
	public getCurrentPdpVersion(): Observable<number> {
		const url = this.path + 'plans-preventions/current-pdp-version';
		let result = this.httpClient.get<number>(url);
		return result;
	}

	// Methode permettant le chargement des impacts pour un PDP
	public getImpacts(pdpId: number): Observable<PlanPreventionEntity> {
		const url = this.path + 'plans-preventions/impacts/' + pdpId;
		let result = this.httpClient.get<PlanPreventionEntity>(url);
		return result;
	}

	// Méthode permettant de mettre à jour les signataires d'un PDP
	public updateSignataires(pdpId: number, command: PdpSignatairesUpdate, forValideurs:boolean): Observable<Object> {
		let url = this.path + 'plans-preventions/' + pdpId + (forValideurs ? '/valideurs-update' : '/signataires-update');
		let request = this.httpClient.post(url, command, {});
		return request;
	}

	// Méthode permettant de mettre à jour les signataires d'un PDP
	public updateSignatairesOnGeneralPdp(pdp: PlanPreventionEntity): Observable<PlanPreventionEntity> {
		let url = this.path + 'plans-preventions/' + pdp.id +'/general/signataires-update';
		let request = this.httpClient.post<PlanPreventionEntity>(url, pdp, {});
		return request;
	}

	// Méthode permettant d'envoyer les envelopes pour un PDP général
	public sendEnvelopeOnGeneralPdp(pdpId: number, destinataires: Destinataire[]): Observable<Object> {
		let url = this.path + 'plans-preventions/' + pdpId + '/general/send-envelopes';
		let request = this.httpClient.post(url, destinataires, {});
		return request;
	}

	// Méthode permettant de mettre à jour les intervenants d'un PDP
	public updateIntervenants(pdpId: number, intervenants: SignatairePdpEntity[]): Observable<Object> {
		let url = this.path + 'plans-preventions/' + pdpId + '/intervenants-update';
		let request = this.httpClient.post(url, intervenants, {});
		return request;
	}

	// Methode permettant le chargement des détail d'une envelope docusign
	public getEnvelope(envelopeId: string): Observable<EnvelopeDocusignEntity> {
		const url = this.path + 'plans-preventions/docusign-envelope/' + envelopeId;
		let result = this.httpClient.get<EnvelopeDocusignEntity>(url);
		return result;
	}

	// Methode permettant le chargement des détail d'une envelope docusign
	public loadPdps(): Observable<PlanPreventionEntity[]> {
		return this.httpClient.get<PlanPreventionEntity[]>(this.path + 'plans-preventions');
	}
	
	// Methode permettant de recuperer les pdp signes par un intervenant
	public listSignedPdp(intervenantId: number, ouvrageId: number, dateLimit: Date): Observable<PlanPreventionTLR[]> {
		let limit = this.jsr310Service.toLocalDateParam(dateLimit);
		let url = this.path + 'plans-preventions?format=TLR&signed-by=' + intervenantId + '&ouvrage=' + ouvrageId + '&date-limit=' + limit;
		let result = this.httpClient.get<PlanPreventionTLR[]>(url);
		return result;
	}
	
	// Méthode permettant d'archiver un PDP
	public archive(id: number): Observable<any> {
		const url = this.path + 'plans-preventions/archive/' + id;
		const request = this.httpClient.put(url, {});
		return request;
	}

	// Méthode permettant d'archiver un PDP
	public supprimer(id: number): Observable<any> {
		const url = this.path + 'plans-preventions/' + id;
		return this.httpClient.delete(url);
	}
	
	// Methode permettant d'exporter les PDP
	public export(exportCriteria: PdpExportCriteria): Observable<Blob> {
		const url = this.path + 'plans-preventions/export';
		return this.httpClient.post(url, exportCriteria, { responseType: 'blob' });
	};

	// Methode permettant la relance d'une envelope docusign
	public relancerEnvelope(docusignEnvelopeId: number): Observable<EnvelopeDocusignEntity> {
		return this.httpClient.get<EnvelopeDocusignEntity>(this.path + 'plans-preventions/docusign/resend-envelope/' + docusignEnvelopeId);
	}

	// Methode permettant l'annulation d'une envelope docusign
	public annulerEnvelope(docusignEnvelopeId: number, pdpId: number): Observable<EnvelopeDocusignEntity> {
		return this.httpClient.delete<EnvelopeDocusignEntity>(this.path + 'plans-preventions/docusign/' + pdpId + '/cancel-envelope/' + docusignEnvelopeId);
	}

	// Méthode permettant de revalider un PDP
	public revalider(id: number): Observable<any> {
		const url = this.path + 'plans-preventions/revalider/' + id;
		const request = this.httpClient.put(url, {});
		return request;
	}
	
	// Méthode permettant de invalider un PDP
	public invalider(id: number): Observable<any> {
		const url = this.path + 'plans-preventions/invalider/' + id;
		const request = this.httpClient.put(url, {});
		return request;
	}
}